import React from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { layoutConsts, horizontalPadding, gutterPadding } from "theme";
import clsx from "clsx";
import SkewedSection from "components/SkewedSection";
import primaryDivideImage from "assets/images/pageSectionSeparators/divide_pink.png";
import secondaryDivideImage from "assets/images/pageSectionSeparators/divide_yellow.png";
import blackDivideImage from "assets/images/pageSectionSeparators/divide_black.png";

const useStyles = (maxWidth: number | "unset") =>
    makeStyles((theme: Theme) => {
        return createStyles({
            root: {
                position: "relative",
                flex: "1 1 auto",
                display: "flex",
            },
            skewedSectionRoot: {
                display: "flex",
                justifyContent: "center",
                flex: "1 1 auto",
            },
            childContainer: {
                flex: "1 1 auto",
                display: "flex",
                maxWidth: maxWidth,
            },
            padding: {
                padding: `${horizontalPadding.md}px ${gutterPadding.md}px`,
                [theme.breakpoints.down("sm")]: {
                    padding: `${horizontalPadding.sm}px ${gutterPadding.sm}px`,
                },
                [theme.breakpoints.down("xs")]: {
                    padding: `${horizontalPadding.xs}px ${gutterPadding.xs}px`,
                },
            },
            topTear: {
                zIndex: 1000,
                position: "absolute",
                width: "100%",
                top: -13,
                "& img": {
                    height: theme.spacing(3),
                    width: "100%",
                },
            },
        });
    });

type IClasses = Partial<ReturnType<ReturnType<typeof useStyles>>>;

type TopSeparatorVariant = "black" | "primary" | "secondary";

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
    skewed?: boolean;
    classes?: IClasses;
    topSeparatorVariant?: "none" | TopSeparatorVariant;
    maxWidth?: number | "unset";
    disablePadding?: boolean;
}

export default function PageSection(props: IProps) {
    const {
        skewed = false,
        topSeparatorVariant = "none",
        maxWidth = layoutConsts.siteContentMaxWidth,
        children,
        classes: overrideClasses,
        className,
        disablePadding = false,
        ...rest
    } = props;

    const classes = useStyles(maxWidth)();

    const getStripImageSrc = (variant: TopSeparatorVariant): string => {
        if (variant === "primary") return primaryDivideImage;
        if (variant === "secondary") return secondaryDivideImage;
        if (variant === "black") return blackDivideImage;

        return blackDivideImage;
    };

    const stripImageSrc =
        topSeparatorVariant === "none" ? undefined : getStripImageSrc(topSeparatorVariant);
    const stripImageAlt = `Page section ${topSeparatorVariant} Strip`;

    return (
        <div className={clsx(className, classes.root, overrideClasses?.root)} {...rest}>
            {typeof stripImageSrc === "undefined" ? null : (
                <SkewedSection
                    className={classes.topTear}
                    skewChildren
                    axis="y"
                    skewAmount={skewed ? undefined : 0}
                >
                    <img src={stripImageSrc} alt={stripImageAlt} />
                </SkewedSection>
            )}
            <SkewedSection
                axis="y"
                skewAmount={skewed ? undefined : 0}
                classes={{
                    root: clsx(classes.skewedSectionRoot, overrideClasses?.skewedSectionRoot),
                    childContainer: clsx(classes.childContainer, overrideClasses?.childContainer, {
                        [classes.padding]: !disablePadding,
                    }),
                }}
            >
                {children}
            </SkewedSection>
        </div>
    );
}
